import { ECalendarMode, ECalendarView, IEventComponentProps } from './types';
import React, { Fragment, useEffect, useState } from 'react';
import { useMiddlewareContext } from './hooks/useMiddlewareContext';
import { DailyViewHorizontal } from './atoms/daily/horizontal/DailyViewHorizontal';
import { DailyViewVertical } from './atoms/daily/DailyViewVertical';
import { DayXView } from './atoms/daily/DayXView';
import { WeeklyView } from './atoms/weekly/WeeklyView';
import { MonthlyView } from './atoms/monthly/MonthlyView';
import { useResources } from './hooks/useResources';

type CalendarViewProps = {
	EventComponent: React.FC<IEventComponentProps>;
	isLoading: boolean;
	resourceGroups: Record<number, number[]> | null;
	resourceId?: number;
	view?: ECalendarView;
};

export const CalendarView: React.FC<CalendarViewProps> = ({
	EventComponent,
	isLoading,
	resourceGroups,
	resourceId,
	view = ECalendarView.VERTICAL,
}) => {
	const { options, dispatchChange } = useMiddlewareContext();
	const { resources } = useResources({ resourceGroups: null });

	const [lastMode, setLastMode] = useState(ECalendarMode.DAILY);

	const { mode = ECalendarMode.DAILY, date = new Date().toISOString() } = options || {};

	const horizontal: boolean = view === ECalendarView.HORIZONTAL;
	const isModeChanged: boolean = mode !== lastMode;

	useEffect(() => {
		setLastMode(mode);
	}, [mode]);

	switch (mode) {
		case ECalendarMode.DAILY:
			if (horizontal) {
				return (
					<DailyViewHorizontal
						key="DailyViewHorizontal"
						EventComponent={EventComponent}
						onAddNewSlotClick={dispatchChange}
						isModeChanged={isModeChanged}
						isLoading={isLoading}
						currentDate={date}
						resourceGroups={resourceGroups}
					/>
				);
			}

			return (
				<DailyViewVertical
					key="DailyViewVertical"
					EventComponent={EventComponent}
					isModeChanged={isModeChanged}
					onAddNewSlotClick={dispatchChange}
					hourSize={options?.hourSize?.vertical}
					isLoading={isLoading}
					currentDate={date}
					resourceGroups={resourceGroups}
				/>
			);

		case ECalendarMode.DAYX:
			return (
				<DayXView
					key="DayXView"
					EventComponent={EventComponent}
					isModeChanged={isModeChanged}
					onAddNewSlotClick={dispatchChange}
					hourSize={options?.hourSize?.vertical}
					isLoading={isLoading}
					currentDate={date}
					resourceGroups={resourceGroups}
					daysCount={options?.dayXDaysCount || 1}
					resourceId={resourceId!} // When provided, shows only this specific resource (like Monthly view)
				/>
			);

		case ECalendarMode.WEEKLY:
			return (
				<WeeklyView
					key="WeeklyView"
					EventComponent={EventComponent}
					isLoading={isLoading}
					resourceGroups={resourceGroups}
				/>
			);

		case ECalendarMode.MONTHLY:
			return (
				<MonthlyView
					key="MonthlyView"
					resourceId={String(resourceId || resources?.[0]?.id)}
					EventComponent={EventComponent}
					isLoading={isLoading}
				/>
			);
		default:
			return <Fragment />;
	}
};

CalendarView.displayName = 'CalendarView';
